/**
 * Builds the `POST /webshop/user/sign_up` body for `paymentinfo.tsx`'s `handleCreateSub`, pulled
 * out into its own module (same reason as `billingAddressSelection.ts`: the host page mounts
 * Stripe/axios on load and is too deep to render-test) so the member-context fields can be unit
 * tested without mounting the whole checkout page.
 *
 * Task 3 put `memberType`/`vat_id`/`company_name` into `curUserInfo` (redux `webshop.curUserInfo`,
 * via Personinfo's `setUserInformation`). This builder forwards them as `member_type`/
 * `buyer_vat_id`/`company_name` so the backend can compute VAT/reverse-charge authoritatively at
 * signup — it NEVER sends a `vat` amount; the old hardcoded `vat: 0` silently told the server
 * "no VAT", which was simply wrong for every taxable order. The server is the only place VAT is
 * actually computed (see `src/utils/checkoutVatSummary.ts`'s doc for the matching DISPLAY-only
 * estimate shown in the summary above this submit).
 */

export interface SignUpBodyInput {
  shoppingCart: Array<{id: any; subscription_length: any}>
  currency: string
  paymentMethod: string
  paymentMethodVal: string
  coupons: Array<{coupon: string}>
  curUserInfo: {
    username?: string
    email?: string
    password?: string
    firstname?: string
    secondname?: string
    country?: string
    countryId?: number
    cityaddress?: string
    streetaddress?: string
    suite?: string
    postalcode?: string
    timezone?: string
    birth?: string
    memberType?: string
    vat_id?: string
    company_name?: string
  }
  sponsorId: number
  billingAddress: Array<any>
  curTermsStatus: boolean
  curDisclaimerStatus: boolean
  curEmailStatus: boolean
}

export function buildSignUpBody({
  shoppingCart,
  currency,
  paymentMethod,
  paymentMethodVal,
  coupons,
  curUserInfo,
  sponsorId,
  billingAddress,
  curTermsStatus,
  curDisclaimerStatus,
  curEmailStatus,
}: SignUpBodyInput) {
  return {
    webshop: {
      plan_ids: shoppingCart.map((item) => {
        return {id: item.id, volume: item.subscription_length}
      }),
      payment_method_body: {
        currency_code: currency, //rapyd bank transfer only
        payment_method: paymentMethod,
      },
      payment_method: paymentMethodVal,
      use_commission_wallet: false,
      amount_commission_wallet: 0,
      use_shopping_credit_wallet: false,
      amount_shopping_credit_wallet: 0,
      forward_url: `${window.location.origin}`,
      coupon_codes: coupons.map((item) => item.coupon),
    },
    user_name: curUserInfo.username,
    email: curUserInfo.email,
    password: curUserInfo.password,
    first_name: curUserInfo.firstname,
    second_name: curUserInfo.secondname,
    sponsor_id: sponsorId,
    country: curUserInfo.country,
    country_id: curUserInfo.countryId,
    cityaddress: curUserInfo.cityaddress,
    streetaddress: curUserInfo.streetaddress,
    suite: curUserInfo.suite,
    postalcode: curUserInfo.postalcode,
    member_type: curUserInfo.memberType || 'personal',
    buyer_vat_id: curUserInfo.vat_id || '',
    company_name: curUserInfo.company_name || '',
    timezone: curUserInfo.timezone,
    birth: curUserInfo.birth,
    billing_address: billingAddress,
    consent: curTermsStatus,
    terms: curDisclaimerStatus,
    notifications: curEmailStatus,
  }
}
